import React, { useState } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const DeleteAccountModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});
  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};

    if (!password.trim()) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRequestDeletion = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/account/delete', {
        password,
        reason: reason.trim() || null
      });

      if (response.success) {
        setPassword('');
        setReason('');
        setErrors({});
        onSuccess?.();
      } else {
        toast.error(response.message || 'Failed to request account deletion. Please try again.');
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to request account deletion. Please try again.';
      toast.error(errorMessage);
      
      // Handle specific error cases
      if (error?.response?.status === 400) {
        // Invalid password or other validation error
        if (errorMessage.toLowerCase().includes('password')) {
          setErrors({ password: errorMessage });
        } else if (errorMessage.toLowerCase().includes('booking')) {
          toast.error('You have active future bookings. Please cancel them before deleting your account.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPassword('');
    setReason('');
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-8 z-10">
        {/* Icon */}
        <div className="flex justify-center mb-5">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
        </div>

        <h2 className="text-xl font-bold text-jet-gray-900 text-center mb-3">Delete Account</h2>
        <p className="text-jet-gray-600 text-sm text-center mb-6">
          This action is permanent and cannot be undone. Your account and all associated data will be deleted after admin approval.
        </p>

        {/* Password Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-jet-gray-700 mb-2">
            Enter Your Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errors.password) {
                setErrors({ ...errors, password: '' });
              }
            }}
            placeholder="Enter your password"
            className={`w-full px-4 py-3 border rounded-md text-sm transition-colors duration-300 focus:outline-none ${
              errors.password
                ? 'border-red-500 focus:border-red-500 focus:shadow-[0_0_0_2px_rgba(239,68,68,0.2)]'
                : 'border-jet-gray-300 focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]'
            }`}
          />
          {errors.password && (
            <span className="text-red-500 text-xs mt-1 block">{errors.password}</span>
          )}
        </div>

        {/* Reason Input (Optional) */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-jet-gray-700 mb-2">
            Reason for Deletion (Optional)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Tell us why you're deleting your account (optional)"
            rows="3"
            className="w-full px-4 py-3 border border-jet-gray-300 rounded-md text-sm transition-colors duration-300 resize-none focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]"
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex-1 border border-jet-gray-300 text-jet-gray-700 py-3 rounded-md text-sm font-semibold hover:bg-jet-gray-50 transition-all duration-200 disabled:opacity-50"
          >
            CANCEL
          </button>
          <button
            onClick={handleRequestDeletion}
            disabled={loading}
            className="flex-1 bg-red-600 text-white py-3 rounded-md text-sm font-semibold hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'SUBMITTING...' : 'DELETE ACCOUNT'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
